import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";

interface SetupInfo {
  setup_fee: string;
  days_to_live: string;
  timeline: {
    step: string;
    duration: string;
    description: string;
  }[];
}

interface SetupTimelineProps {
  setupInfo: SetupInfo;
}

export const SetupTimeline = ({ setupInfo }: SetupTimelineProps) => {
  // Return null if timeline data is missing
  if (!setupInfo?.timeline || setupInfo.timeline.length === 0) {
    return null;
  }

  return (
    <Card className="p-6 md:p-8">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b">
          <div>
            <h3 className="text-2xl font-bold mb-2">Setup & Launch Timeline</h3>
            <p className="text-muted-foreground">From kickoff to your first results</p>
          </div>
          <div className="flex gap-4 sm:gap-6">
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground">Setup Fee</div>
              <div className="text-xs sm:text-sm font-semibold text-foreground">{setupInfo.setup_fee}</div>
            </div>
            <div>
              <div className="text-xs sm:text-sm text-muted-foreground">Days to Live</div>
              <div className="text-xs sm:text-sm font-semibold text-foreground">{setupInfo.days_to_live}</div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Progress Line - Desktop */}
          <div className="hidden md:block absolute top-6 left-0 right-0 h-1 bg-muted">
            <div className="absolute top-0 left-0 h-full w-full bg-primary"></div>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-4 gap-12 md:gap-4 relative">
            {setupInfo.timeline.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector Line - Mobile (behind content) */}
                {index < setupInfo.timeline.length - 1 && (
                  <div className="md:hidden absolute left-5 sm:left-6 top-12 w-0.5 bg-border h-full"></div>
                )}
                
                {/* Step Number */}
                <div className="flex items-start gap-3 mb-3 relative z-10">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-base sm:text-lg shrink-0">
                    {index + 1}
                  </div>
                  <Badge variant="secondary" className="md:hidden text-xs">{step.duration}</Badge>
                </div>

                {/* Content */}
                <div className="ml-0 md:ml-0 relative z-10 bg-card">
                  <div className="hidden md:block text-xs text-muted-foreground mb-1">{step.duration}</div>
                  <h4 className="font-bold mb-2 text-sm sm:text-base">{step.step}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-6 border-t flex items-center gap-3 text-sm">
          <CheckCircle2 className="h-5 w-5 text-primary" />
          <span className="text-muted-foreground">
            <strong>Done-for-you setup</strong> - Our team handles the technical configuration so you can focus on closing deals
          </span>
        </div>
      </div>
    </Card>
  );
};
